import { prettyPrintDate } from "@prismicio/editor-support/Date"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { useOnChange } from "@prismicio/editor-support/React"
import { formatBytes } from "@prismicio/editor-support/String"
import { isDefined } from "@prismicio/editor-support/Type"
import {
  Avatar,
  Box,
  ContentEditable,
  ErrorBoundary,
  FormInput,
  FormInputField,
  Skeleton,
  Tab,
  Text,
} from "@prismicio/editor-ui"
import type { Editor } from "@tiptap/core"
import { Suspense, useMemo, useState } from "react"

import { type EditorConfig, useEditorConfig } from "../../../../../EditorConfig"
import { getTitleWithoutExtension } from "../../../../imageUtils"
import {
  type MediaAsset,
  mediaAssetType,
} from "../../../hooks/mediaLibraryData"
import { useUploaders } from "../../../hooks/uploadersData"
import { useSelectedMedia } from "../../../hooks/useSelectedMedia"
import { useMediaLibrary } from "../../MediaLibraryContext"
import { useMediaLibraryToast } from "../../MediaLibraryToast"
import { MultiMediaMetadataTags } from "./SidepaneMetadataTags/MultiMediaMetadataTags"
import { SingleMediaMetadataTags } from "./SidepaneMetadataTags/SingleMediaMetadataTags"

export function SidepaneMetadata() {
  const [tab, setTab] = useState<"description" | "more-info">("description")
  const selectedMedia = useSelectedMedia()

  if (selectedMedia.length !== 1) {
    return <MultipleMediaMetadataContent />
  }

  const isDescriptionTab = tab === "description"
  const isMoreInfoTab = tab === "more-info"

  return (
    <>
      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr"
        backgroundColor="grey2"
        border
        borderStyle="solid"
        borderRadius={8}
      >
        <Tab
          selected={isDescriptionTab}
          outline={isDescriptionTab}
          transparent
          onClick={() => setTab("description")}
        >
          Description
        </Tab>
        <Tab
          selected={isMoreInfoTab}
          outline={isMoreInfoTab}
          transparent
          onClick={() => setTab("more-info")}
        >
          More info
        </Tab>
      </Box>

      <TabContent tab={tab} />
    </>
  )
}

interface TabContentProps {
  tab: "description" | "more-info"
}

function TabContent(props: TabContentProps) {
  const { tab } = props

  switch (tab) {
    case "description":
      return <DescriptionTab />
    case "more-info":
      return <MoreInfoTab />
  }
}

function DescriptionTab() {
  const { title, alt, onBlur, isUploading, selectedMedia } = useUpdateMetadata()

  if (!selectedMedia) return null

  const { extension } = selectedMedia

  return (
    <Box flexDirection="column" gap={16}>
      <FormInput
        value={title.value}
        onValueChange={title.set}
        label="Media title"
        placeholder="Media title"
        onBlur={onBlur}
        disabled={isUploading}
        suffix={extension ? `.${extension}` : undefined}
      />

      <FormInputField label="Alternative text">
        <ContentEditable
          content={alt.displayValue}
          contentVersion={alt.version}
          readOnly={isUploading}
          placeholder="A short description of the media for accessibility"
          multiline
          decorations={{ em: "highlight" }}
          onChange={alt.onChange}
          onBlur={onBlur}
        />
      </FormInputField>
      {!isUploading && (
        <SingleMediaMetadataTags selectedMedia={selectedMedia} />
      )}
    </Box>
  )
}

function MoreInfoTab() {
  const { credits, notes, onBlur, isUploading, selectedMedia } =
    useUpdateMetadata()

  if (!selectedMedia) return null

  const { uploader_id } = selectedMedia

  const content = createMetadataContent(selectedMedia)

  return (
    <Box gap={8} flexDirection="column">
      {content.map((item) => (
        <Box key={item.label} gap={8}>
          <Text color="grey11">{item.label}:</Text>
          <Text>{item.value}</Text>
        </Box>
      ))}
      <Box gap={8}>
        <Text color="grey11">Uploaded by:</Text>
        <ErrorBoundary
          renderError={() => <Text color="grey11">Error getting uploader</Text>}
        >
          <Suspense fallback={<UploaderSkeleton />}>
            <Uploader uploaderId={uploader_id} />
          </Suspense>
        </ErrorBoundary>
      </Box>
      <FormInputField label="Copyright">
        <ContentEditable
          content={credits.displayValue}
          contentVersion={credits.version}
          readOnly={isUploading}
          placeholder="Media copyrights or credits"
          multiline
          decorations={{ em: "highlight" }}
          onChange={credits.onChange}
          onBlur={onBlur}
        />
      </FormInputField>

      <FormInputField label="Private notes">
        <ContentEditable
          content={notes.displayValue}
          contentVersion={notes.version}
          readOnly={isUploading}
          placeholder="Private notes are never shared publicly"
          multiline
          decorations={{ em: "highlight" }}
          onChange={notes.onChange}
          onBlur={onBlur}
        />
      </FormInputField>
    </Box>
  )
}

function Uploader(props: { uploaderId?: string }) {
  const { uploaderId } = props

  const { uploadersById } = useUploaders()

  const uploader = uploadersById.get(uploaderId ?? "ghost")

  // Not known for now.
  if (uploaderId && !uploader) return <UploaderSkeleton />

  // Not known, never will be.
  if (!uploader) return null

  return (
    <>
      <Avatar
        size={24}
        firstName={uploader.firstName}
        lastName={uploader.lastName}
        imageUrl={uploader.uploadedAvatar}
        hasBorder={false}
      />
      <Text>{uploader.name}</Text>
    </>
  )
}

function UploaderSkeleton() {
  return <Skeleton width={128} />
}

function MultipleMediaMetadataContent() {
  const selectedMedia = useSelectedMedia()
  const { uploadingFiles } = useMediaLibrary()

  const isUploading = useMemo(
    () =>
      uploadingFiles.some(
        (file) =>
          selectedMedia.some((media) => file.id === media.id) &&
          file.status !== "success",
      ),
    [selectedMedia, uploadingFiles],
  )

  return (
    <>
      {!isUploading && <MultiMediaMetadataTags selectedMedia={selectedMedia} />}
    </>
  )
}

function useUpdateMetadata() {
  const [selectedMedia] = useSelectedMedia()
  const { updateMedia, uploadingFiles } = useMediaLibrary()
  const config = useEditorConfig()
  const { showToast } = useMediaLibraryToast()

  const selectedMediaId = selectedMedia?.id ?? ""

  const alt = useMetaField(selectedMedia, "alt")
  const notes = useMetaField(selectedMedia, "notes")
  const credits = useMetaField(selectedMedia, "credits")
  const title = useMetaField(
    selectedMedia,
    "filename",
    getTitleWithoutExtension,
  )

  const isUploading = uploadingFiles.some(
    (file) => file.id === selectedMedia?.id && file.status !== "success",
  )

  async function onBlur() {
    if (!selectedMedia) return

    if (!title.changed && !alt.changed && !credits.changed && !notes.changed)
      return

    const { extension, search_highlight } = selectedMedia
    const filename = extension ? `${title.value}.${extension}` : title.value

    updateMedia(selectedMediaId, {
      filename,
      alt: alt.value,
      credits: credits.value,
      notes: notes.value,
      // Update the search highlights so that we discard them as soon as we updated the underlying data.
      // This ensures we stop displaying the outdated highlight instead of the newly updated meta.
      search_highlight: {
        alt: alt.changed ? undefined : search_highlight?.alt,
        credits: credits.changed ? undefined : search_highlight?.credits,
        filename: title.changed ? undefined : search_highlight?.filename,
        notes: notes.changed ? undefined : search_highlight?.notes,
      },
    })

    await updateMetadata({
      id: selectedMediaId,
      config,
      metadata: {
        filename,
        alt: alt.value,
        credits: credits.value,
        notes: notes.value,
      },
    })

    showToast({ title: "Media metadata has been saved.", seconds: 3 })
  }

  return {
    selectedMedia,
    onBlur,
    isUploading,
    title,
    alt,
    credits,
    notes,
    extension: selectedMedia?.extension,
  }
}

function useMetaField(
  media: MediaAsset | undefined,
  name: "filename" | "alt" | "credits" | "notes",
  transformValue = (media?: MediaAsset) => media?.[name],
) {
  const initialValue = transformValue(media) ?? ""

  const [value, setValue] = useState(initialValue)
  useOnChange(media?.id, () => setValue(initialValue))

  const highlightedValue = media?.search_highlight?.[name]
  const isHighlight = Boolean(highlightedValue)

  // When showing the highlights, we want them gone as soon as the user starts editing the field as the highlight may no longer be relevant.
  function onChange(editor: Editor) {
    const { from, to } = editor.state.selection

    editor.commands.selectAll()
    editor.commands.unsetAllMarks()
    editor.commands.setTextSelection({ from, to })

    // Save without any HTML.
    setValue(editor.getText())
  }

  return {
    value,
    // Use this instead of onChange when not using ContentEditable.
    set: setValue,
    displayValue: newlineToHtml(highlightedValue ?? value) ?? "",
    onChange,
    version: `${media?.id ?? ""}_${isHighlight}`,
    changed: value !== initialValue,
  }
}

// Multiline media metas are raw text with newline characters.
// Multiline media highlights are strings with some newline characters and some HTML tags.
// This ensures both are converted to full HTML, ready to be displayed in a ContentEditable.
function newlineToHtml(content?: string) {
  return content?.replace(/\n/g, "<br>")
}

interface UpdateMetadataParams {
  id: string
  config: EditorConfig
  metadata: {
    filename?: string
    alt?: string
    credits?: string
    notes?: string
  }
}

function updateMetadata(params: UpdateMetadataParams) {
  const { id, config, metadata } = params
  if (!config.mediaLibrary) throw new Error("Media Library not configured.")

  const { assetsApiBaseUrl, repository, authStrategy } = config

  return request(new URL(`assets/${id}`, assetsApiBaseUrl), {
    headers: { repository },
    body: { ...metadata },
    method: "PATCH",
    credentials: getRequestCredentials(authStrategy),
    okType: mediaAssetType,
  })
}

function createMetadataContent(selectedMedia: MediaAsset) {
  const { width, height, last_modified, size, extension } = selectedMedia
  const hasDimensions = width && height

  const date = {
    label: "Date",
    value: prettyPrintDate(new Date(last_modified)),
  }

  const fileSize = {
    label: "File size",
    value: formatBytes(size),
  }

  const dimensions = hasDimensions
    ? {
        label: "Dimensions",
        value: `${width}×${height}`,
      }
    : undefined

  const ratio = hasDimensions
    ? {
        label: "Ratio",
        value: ((width ?? 0) / (height ?? 0)).toFixed(1),
      }
    : undefined

  const extensionValue = extension
    ? {
        label: "Extension",
        value: extension.toUpperCase(),
      }
    : undefined

  return [date, fileSize, dimensions, ratio, extensionValue].filter(isDefined)
}
